/**
 * MobileOrdersBell — polls /api/mobile-store/orders/summary every 30s.
 * On detected increase in pending count:
 *   - fires a browser Notification (if permission granted)
 *   - plays a soft chime (WebAudio, no assets needed)
 *   - shows a sonner toast
 * Renders a header bell with a live count badge. Click → /app/mobile-store?tab=orders
 */
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShoppingBag } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { toast } from "sonner";

const POLL_MS = 30_000;
const ROLES_ALLOWED = new Set(["super_admin", "admin", "chief_accountant", "store_manager", "cashier", "showroom_admin", "showroom_sales_manager"]);

function playChime() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const now = ctx.currentTime;
    // Two-note bakery-friendly chime: C6 → E6
    [1046.5, 1318.5].forEach((freq, i) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = "sine";
      osc.frequency.value = freq;
      const start = now + i * 0.14;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.22, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.35);
      osc.connect(gain).connect(ctx.destination);
      osc.start(start);
      osc.stop(start + 0.4);
    });
    setTimeout(() => ctx.close(), 900);
  } catch (_) { /* ignore */ }
}

export function MobileOrdersBell({ user }) {
  const nav = useNavigate();
  const role = user?.role_id || user?.role;
  const enabled = role && ROLES_ALLOWED.has(role);

  const [pending, setPending] = useState(0);
  const [total, setTotal] = useState(0);
  const [permission, setPermission] = useState(typeof Notification !== "undefined" ? Notification.permission : "default");
  const prevPendingRef = useRef(null);
  const initialisedRef = useRef(false);

  useEffect(() => {
    if (!enabled) return undefined;
    let cancelled = false;

    const tick = async () => {
      try {
        const { data } = await api.get("/mobile-store/orders/summary");
        if (cancelled) return;
        const p = Number(data?.counts?.pending || 0);
        const tot = Number(data?.total || 0);
        setPending(p);
        setTotal(tot);
        const prev = prevPendingRef.current;
        // Only alert on transitions AFTER the first tick, and when count increased
        if (initialisedRef.current && prev !== null && p > prev) {
          const delta = p - prev;
          const msg = delta === 1 ? "New mobile order received" : `${delta} new mobile orders received`;
          toast.success(msg, {
            description: `You now have ${p} pending order${p === 1 ? "" : "s"} awaiting dispatch.`,
            action: { label: "View", onClick: () => nav("/app/mobile-store") },
          });
          playChime();
          if (typeof Notification !== "undefined" && Notification.permission === "granted") {
            try {
              const n = new Notification("🍰 New Bakemill order", {
                body: `${msg} — ${p} pending orders total.`,
                tag: "bakemill-mobile-order",
                renotify: true,
              });
              n.onclick = () => { window.focus(); nav("/app/mobile-store"); n.close(); };
            } catch (_) { /* ignore */ }
          }
        }
        prevPendingRef.current = p;
        initialisedRef.current = true;
      } catch (_) { /* silent — endpoint may 403 for some roles */ }
    };
    tick();
    const id = setInterval(tick, POLL_MS);
    return () => { cancelled = true; clearInterval(id); };
  }, [enabled, nav]);

  const askPermission = async () => {
    if (typeof Notification === "undefined") return;
    if (Notification.permission === "default") {
      const p = await Notification.requestPermission();
      setPermission(p);
      if (p === "granted") toast.success("Desktop notifications enabled");
    }
  };

  if (!enabled) return null;

  const hasPending = pending > 0;
  const needsPerm = permission === "default";

  return (
    <button
      onClick={() => {
        if (needsPerm) askPermission();
        nav("/app/mobile-store");
      }}
      data-testid="mobile-orders-bell"
      title={needsPerm ? "Click to enable desktop notifications for new orders" : `${pending} pending / ${total} total mobile orders`}
      className={`relative flex items-center gap-1.5 h-9 px-2.5 rounded-md border transition ${
        hasPending
          ? "bg-amber-50 border-amber-300 text-amber-900 hover:bg-amber-100 animate-pulse"
          : "bg-white border-[#EAE5D9] text-[#5C4033] hover:bg-[#FAF6EE]"
      }`}
    >
      <ShoppingBag className="w-4 h-4" />
      <span className="text-xs font-medium hidden sm:inline">Mobile</span>
      {hasPending && (
        <Badge
          data-testid="mobile-orders-bell-badge"
          className="min-w-[20px] h-5 px-1.5 bg-[#C05621] text-white border-0 text-[10px] font-bold"
        >
          {pending > 99 ? "99+" : pending}
        </Badge>
      )}
      {needsPerm && !hasPending && (
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-500 rounded-full" title="Click to enable notifications" />
      )}
    </button>
  );
}
